import { useState, type ReactNode } from 'react';
import { FileText, Download } from 'lucide-react';
import type { ChatAttachmentMeta } from '../../lib/chat';
import { humanSize, isPdf } from './attachmentUtils';
import { FilePreviewModal } from './FilePreviewModal';

// Re-export so existing imports keep working.
export { humanSize } from './attachmentUtils';

/**
 * Renders a chat attachment (image / video / document) from its message
 * metadata, with an inline preview: images/videos show inline, PDFs show a
 * thumbnail, and clicking a previewable file opens a full preview modal.
 * Shared by the Team Chat page and the Let's Chat bubble.
 */
export function ChatAttachment({ meta }: { meta: ChatAttachmentMeta }) {
  const [preview, setPreview] = useState(false);
  const pdf = isPdf(meta);

  let content: ReactNode;

  if (meta.media === 'image') {
    content = (
      <button type="button" onClick={() => setPreview(true)} className="block">
        <img
          src={meta.url}
          alt={meta.filename}
          className="max-h-64 max-w-full rounded-lg border border-neutral-200 object-cover"
        />
      </button>
    );
  } else if (meta.media === 'video') {
    content = (
      <video src={meta.url} controls className="max-h-64 max-w-full rounded-lg border border-neutral-200" />
    );
  } else if (pdf) {
    // PDF: inline thumbnail (page 1) + file bar, click to open the full preview.
    content = (
      <button
        type="button"
        onClick={() => setPreview(true)}
        className="block w-[240px] max-w-full overflow-hidden rounded-lg border border-neutral-200 bg-white text-left"
      >
        <div className="relative h-36 w-full bg-neutral-100">
          <iframe
            src={`${meta.url}#toolbar=0&navpanes=0&view=FitH`}
            title={meta.filename}
            className="pointer-events-none absolute inset-0 size-full"
            tabIndex={-1}
          />
        </div>
        <div className="flex items-center gap-2 border-t border-neutral-200 px-3 py-2">
          <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-pink-50 text-pink-600">
            <FileText className="size-4" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-medium text-neutral-800">{meta.filename}</span>
            <span className="block text-[11px] text-neutral-400">{humanSize(meta.bytes)}</span>
          </span>
        </div>
      </button>
    );
  } else {
    // Other documents: direct download card.
    content = (
      <a
        href={meta.url}
        target="_blank"
        rel="noopener noreferrer"
        download={meta.filename}
        className="flex max-w-[240px] items-center gap-3 rounded-lg border border-neutral-200 bg-white px-3 py-2 transition-colors hover:bg-neutral-50"
      >
        <span className="flex size-9 shrink-0 items-center justify-center rounded-md bg-pink-50 text-pink-600">
          <FileText className="size-5" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium text-neutral-800">{meta.filename}</span>
          <span className="block text-[11px] text-neutral-400">{humanSize(meta.bytes)}</span>
        </span>
        <Download className="size-4 shrink-0 text-neutral-400" />
      </a>
    );
  }

  return (
    <>
      {content}
      {preview && <FilePreviewModal meta={meta} onClose={() => setPreview(false)} />}
    </>
  );
}
